import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart,
  ArrowRight,
  Waves,
  Megaphone,
  Trophy,
  Users,
} from 'lucide-react';
import { WavesLedgerModal, Avatar, Modal } from '../../components/UI';
import { useAuthStore } from '../../store/authStore';
import { analyticsApi, appreciationsApi, announcementsApi, eventsApi, streamsApi } from '../../services/api';
import { DashboardStats, Appreciation, Announcement, EventTypeConfig, StreamThread } from '../../types';
import MentionText from '../../components/MentionText/MentionText';
import FormattedText from '../../utils/formatText';
import { getEventIconComponent } from '../../utils/eventIcons';
import styles from './Dashboard.module.css';

type TimePeriod = 'week' | 'month' | 'quarter' | 'year' | 'custom';

const RIPPLE_TYPES: Record<string, { label: string; emoji: string; color: string; bg: string }> = {
  kudos: { label: 'Kudos', emoji: '👏', color: '#8B5CF6', bg: '#F5F3FF' },
  shoutout: { label: 'Shoutout', emoji: '📣', color: '#EC4899', bg: '#FDF2F8' },
  thank_you: { label: 'Thank You', emoji: '🙏', color: '#6366F1', bg: '#EEF2FF' },
  great_job: { label: 'Great Job', emoji: '🏆', color: '#D97706', bg: '#FFFBEB' },
  team_player: { label: 'Team Player', emoji: '🤝', color: '#059669', bg: '#ECFDF5' },
  innovator: { label: 'Innovator', emoji: '💡', color: '#0EA5E9', bg: '#F0F9FF' },
  customer_hero: { label: 'Customer Hero', emoji: '❤️', color: '#DC2626', bg: '#FEF2F2' },
};

const PRIORITY_CONFIG: Record<string, { color: string; bg: string; dot: string }> = {
  urgent: { color: '#DC2626', bg: '#FEF2F2', dot: '#EF4444' },
  high: { color: '#D97706', bg: '#FFFBEB', dot: '#F59E0B' },
  medium: { color: '#6366F1', bg: '#EEF2FF', dot: '#818CF8' },
  low: { color: '#64748B', bg: '#F1F5F9', dot: '#94A3B8' },
};

const COLORS = ['#0C6B62', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentAppreciations, setRecentAppreciations] = useState<Appreciation[]>([]);
  const [recentAnnouncements, setRecentAnnouncements] = useState<Announcement[]>([]);
  const [recentStreams, setRecentStreams] = useState<StreamThread[]>([]);
  const [unreadAnnouncementCount, setUnreadAnnouncementCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('month');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customDates, setCustomDates] = useState({ start: '', end: '' });
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [leaderTab, setLeaderTab] = useState<'received' | 'given'>('received');
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  const [eventTypes, setEventTypes] = useState<EventTypeConfig[]>([]);
  const [eventReactions, setEventReactions] = useState<Record<string, { count: number; userReacted: boolean }>>({});
  const [showReactionModal, setShowReactionModal] = useState(false);
  const [reactionModalData, setReactionModalData] = useState<any[]>([]);
  const [reactionModalTitle, setReactionModalTitle] = useState('');

  const loadDashboard = useCallback(async (period: TimePeriod) => {
    if (period === 'custom') return;
    try {
      const periodDays = period === 'month' ? 30 : period === 'quarter' ? 90 : 365;

      // Stats API is fast — fetch it first for KPIs
      const statsRes = await analyticsApi.getDashboard(periodDays);
      setStats(statsRes.data);

      // Recent items — always fetch last 30 days for the card list (fast)
      // KPI numbers come from the stats API above (already filtered)
      const recentDays = Math.min(periodDays, 30);
      const [appreciationsRes, announcementsRes, streamsRes] = await Promise.all([
        appreciationsApi.getFeed({ page: 1, days: recentDays }),
        announcementsApi.getAll({ page: 1, days: recentDays }),
        streamsApi.getThreads({ page: 1, days: recentDays }).catch(() => ({ data: { threads: [] } })),
      ]);
      setRecentAppreciations(appreciationsRes.data.appreciations.slice(0, 3));
      const allAnnouncements = announcementsRes.data.announcements;
      setRecentAnnouncements(allAnnouncements.slice(0, 3));
      setUnreadAnnouncementCount(allAnnouncements.filter((a: Announcement) => !a.is_read).length);
      setRecentStreams((streamsRes.data.threads || []).slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDashboard(timePeriod);
  }, [timePeriod, loadDashboard]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const [eventsRes, typesRes] = await Promise.all([
          eventsApi.getByMonth(new Date().getFullYear(), new Date().getMonth() + 1),
          eventsApi.getEventTypes(),
        ]);
        const today = new Date().getDate();
        const upcoming = (eventsRes.data.events || [])
          .filter((e: any) => new Date(e.event_date).getDate() >= today)
          .slice(0, 4);
        setUpcomingEvents(upcoming);
        setEventTypes(typesRes.data.eventTypes || []);

        // Fetch reactions/wishes for upcoming events
        const reactionsMap: Record<string, { count: number; userReacted: boolean }> = {};
        const realEvents = upcoming.filter((e: any) => !String(e.id).startsWith('birthday-') && !String(e.id).startsWith('anniversary-'));
        const synEvents = upcoming.filter((e: any) => String(e.id).startsWith('birthday-') || String(e.id).startsWith('anniversary-'));

        if (realEvents.length > 0) {
          const results = await Promise.all(
            realEvents.map((e: any) => eventsApi.getEventReactions(e.id).catch(() => null))
          );
          realEvents.forEach((e: any, i: number) => {
            const r = results[i]?.data;
            if (r) reactionsMap[e.id] = { count: r.count, userReacted: r.user_reacted };
          });
        }

        if (synEvents.length > 0) {
          const results = await Promise.all(
            synEvents.map((e: any) => {
              const idStr = String(e.id);
              const prefix = idStr.startsWith('birthday-') ? 'birthday-' : 'anniversary-';
              const synType = prefix === 'birthday-' ? 'birthday' : 'work_anniversary';
              const rest = idStr.slice(prefix.length).split('-');
              if (rest.length < 6) return null;
              const userId = rest.slice(0, 5).join('-');
              const year = parseInt(rest[5], 10);
              return eventsApi.getEventWishes(userId, synType, year).catch(() => null);
            })
          );
          synEvents.forEach((e: any, i: number) => {
            const r = results[i]?.data;
            if (r) reactionsMap[e.id] = { count: r.wish_count || r.count || 0, userReacted: r.user_wished || false };
          });
        }

        setEventReactions(reactionsMap);
      } catch { /* ignore */ }
    };
    fetchEvents();
  }, []);

  const fetchCustomData = async () => {
    if (!customDates.start || !customDates.end) return;
    try {
      const statsRes = await analyticsApi.getDashboard(undefined, customDates.start, customDates.end);
      const dateFilter = { start_date: customDates.start, end_date: customDates.end };
      const [appreciationsRes, announcementsRes, streamsRes] = await Promise.all([
        appreciationsApi.getFeed({ page: 1, ...dateFilter }),
        announcementsApi.getAll({ page: 1, ...dateFilter }),
        streamsApi.getThreads({ page: 1, ...dateFilter }).catch(() => ({ data: { threads: [] } })),
      ]);
      setStats(statsRes.data);
      setRecentAppreciations(appreciationsRes.data.appreciations.slice(0, 3));
      const allAnnouncements = announcementsRes.data.announcements;
      setRecentAnnouncements(allAnnouncements.slice(0, 3));
      setUnreadAnnouncementCount(allAnnouncements.filter((a: Announcement) => !a.is_read).length);
      setRecentStreams((streamsRes.data.threads || []).slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimePeriodChange = (period: TimePeriod) => {
    if (period === 'custom') {
      setShowDatePicker(true);
    } else {
      setTimePeriod(period);
    }
  };

  const handleCustomDateApply = () => {
    if (customDates.start && customDates.end) {
      setTimePeriod('custom');
      setShowDatePicker(false);
      fetchCustomData();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatRelativeTime = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffH = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    if (diffH < 1) return 'Just now';
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return formatDate(dateString);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getInitials = (first?: string, last?: string) => {
    return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
  };

  const getTypeConfig = (type: string) => {
    const et = eventTypes.find(t => t.key === type);
    return {
      color: et?.color || '#6366F1',
      icon_name: et?.icon_name || (type === 'birthday' ? 'cake' : type === 'work_anniversary' ? 'briefcase' : 'party'),
      label: et?.name || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    };
  };

  const getDaysUntil = (dateString: string) => {
    const now = new Date(); now.setHours(0,0,0,0);
    const d = new Date(dateString); d.setHours(0,0,0,0);
    return Math.round((d.getTime() - now.getTime()) / 86400000);
  };

  const handleViewReactions = async (eventId: string, eventTitle: string) => {
    try {
      const res = await eventsApi.getEventReactionDetails(eventId);
      setReactionModalData(res.data);
      setReactionModalTitle(eventTitle);
      setShowReactionModal(true);
    } catch (err) { console.error(err); }
  };

  // Bucket real trend data based on selected time period
  const activityData = useMemo(() => {
    const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    if (timePeriod === 'week') {
      // 7 bars — one per day of the week
      const buckets = Array(7).fill(0);
      (stats?.trend || []).forEach((t: any) => {
        const d = new Date(t.date);
        const day = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
        buckets[day] += Number(t.count);
      });
      return { labels: DAY_LABELS, data: buckets };
    }

    if (timePeriod === 'month') {
      // 4 weekly buckets
      const buckets = Array(4).fill(0);
      const trend = stats?.trend || [];
      if (trend.length > 0) {
        const sorted = [...trend].sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const startDate = new Date(sorted[0].date);
        sorted.forEach((t: any) => {
          const diff = Math.floor((new Date(t.date).getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          const bucket = Math.min(3, Math.floor(diff / 7));
          buckets[bucket] += Number(t.count);
        });
      }
      return { labels: ['W1', 'W2', 'W3', 'W4'], data: buckets };
    }

    // year: 12 monthly buckets
    const buckets = Array(12).fill(0);
    (stats?.trend || []).forEach((t: any) => {
      const month = new Date(t.date).getMonth(); // 0-11
      buckets[month] += Number(t.count);
    });
    return { labels: MONTH_LABELS, data: buckets };
  }, [stats, timePeriod]);
  const maxRipple = Math.max(...activityData.data, 1);

  // SVG Donut for company values
  const donutData = useMemo(() => {
    if (!stats?.byCompanyValue || stats.byCompanyValue.length === 0) return null;
    const data = stats.byCompanyValue.slice(0, 4).map((v, i) => ({
      name: v.name,
      count: Number(v.count),
      color: v.color || COLORS[i % COLORS.length],
    }));
    const total = data.reduce((s, d) => s + d.count, 0);
    return { data, total };
  }, [stats]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.greeting}>{getGreeting()}, {user?.first_name}</h1>
          <p className={styles.subtitle}>
            {recentAppreciations.length > 0
              ? `${stats?.stats.total_appreciations || 0} ripples went out at Matasma in ${timePeriod === 'month' ? 'last 30 days' : timePeriod === 'quarter' ? 'last 90 days' : timePeriod === 'year' ? 'this year' : 'selected period'}.`
              : "Here's what's happening in Matasma today!"}
          </p>
        </div>
        <div className={styles.timeFilterGroup}>
          {([
            { key: 'month' as TimePeriod, label: 'Last 30 days' },
            { key: 'quarter' as TimePeriod, label: 'Last 90 days' },
            { key: 'year' as TimePeriod, label: 'This year' },
            { key: 'custom' as TimePeriod, label: 'Custom' },
          ]).map(t => (
            <button
              key={t.key}
              className={`${styles.timeFilterBtn} ${timePeriod === t.key ? styles.timeFilterActive : ''}`}
              onClick={() => handleTimePeriodChange(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Stats Strip ── */}
      <div className={styles.statsStrip}>
        <div className={styles.statCell} onClick={() => navigate('/announcements')}>
          <div className={styles.statTop}>
            <div className={styles.statIcon} style={{ background: '#f7e4f3' }}>
              <Megaphone size={14} color="#b24fa0" />
            </div>
            <span className={styles.statLabel}>ANNOUNCEMENTS</span>
          </div>
          <span className={styles.statValue}>{stats?.stats.total_announcements || recentAnnouncements.length}</span>
          <span className={styles.statDelta}>
            {recentAnnouncements.filter(a => a.is_pinned).length} pinned · {recentAnnouncements.filter(a => a.priority === 'urgent').length} urgent
          </span>
          <div className={styles.statBar}><div className={styles.statBarFill} style={{ width: '60%', background: '#b24fa0' }} /></div>
        </div>

        <div className={styles.statCell} onClick={() => navigate('/feed')}>
          <div className={styles.statTop}>
            <div className={styles.statIcon} style={{ background: '#dff0ec' }}>
              <Waves size={14} color="#0f7a68" />
            </div>
            <span className={styles.statLabel}>RIPPLES</span>
          </div>
          <span className={styles.statValue}>{stats?.stats.total_appreciations || 0}</span>
          <span className={styles.statDelta}>
            in {timePeriod === 'month' ? 'last 30 days' : timePeriod === 'quarter' ? 'last 90 days' : timePeriod === 'year' ? 'this year' : 'selected period'}
          </span>
          <div className={styles.statBar}><div className={styles.statBarFill} style={{ width: '80%', background: '#0f7a68' }} /></div>
        </div>

        <div className={styles.statCell} onClick={() => setShowLedgerModal(true)}>
          <div className={styles.statTop}>
            <div className={styles.statIcon} style={{ background: '#e0edfa' }}>
              <Heart size={14} color="#2f7fd1" />
            </div>
            <span className={styles.statLabel}>WAVES GIVEN</span>
          </div>
          <span className={styles.statValue}>{stats?.stats.total_points_given || 0}</span>
          <span className={styles.statDelta}>{user?.points_balance || 0} left to give</span>
          <div className={styles.statBar}><div className={styles.statBarFill} style={{ width: '75%', background: '#2f7fd1' }} /></div>
        </div>

        <div className={styles.statCell} onClick={() => setShowLedgerModal(true)}>
          <div className={styles.statTop}>
            <div className={styles.statIcon} style={{ background: '#fbeccd' }}>
              <Trophy size={14} color="#e8a93c" />
            </div>
            <span className={styles.statLabel}>WAVES RECEIVED</span>
          </div>
          <span className={styles.statValue}>{stats?.stats.total_points_received || 0}</span>
          <span className={styles.statDelta}>
            in {timePeriod === 'month' ? 'last 30 days' : timePeriod === 'quarter' ? 'last 90 days' : timePeriod === 'year' ? 'this year' : 'selected period'}
          </span>
          <div className={styles.statBar}><div className={styles.statBarFill} style={{ width: '90%', background: '#e8a93c' }} /></div>
        </div>

        <div className={styles.statCell} onClick={() => navigate('/streams')}>
          <div className={styles.statTop}>
            <div className={styles.statIcon} style={{ background: '#fbe7e0' }}>
              <Users size={14} color="#e8613c" />
            </div>
            <span className={styles.statLabel}>OPEN STREAMS</span>
          </div>
          <span className={styles.statValue}>{recentStreams.length}</span>
          <span className={styles.statDelta}>{recentStreams.filter((s: any) => !s.is_read).length} unread</span>
          <div className={styles.statBar}><div className={styles.statBarFill} style={{ width: '40%', background: '#e8613c' }} /></div>
        </div>
      </div>

      {/* ── Main Grid ── */}
      <div className={styles.mainGrid}>
        {/* Left Column */}
        <div className={styles.leftColumn}>
          {/* Latest Ripple — always show the most recent, text only (no media) */}
          {recentAppreciations.length > 0 && (() => {
            const top = recentAppreciations[0];
            const t = RIPPLE_TYPES[top.type] || { label: top.type, emoji: '💖', color: '#e8a93c', bg: '#F0FDFA' };
            const recipients = top.recipients && top.recipients.length > 1
              ? top.recipients.map((rec: any) => rec.to_first_name).join(' & ')
              : top.to_first_name;
            return (
              <div className={styles.featuredCard} onClick={() => navigate(`/feed?highlight=${top.id}`)}>
                <div className={styles.featuredDecor1} />
                <div className={styles.featuredDecor2} />
                <div className={styles.featuredRipple}><span /><span /><span /></div>
                <div className={styles.featuredLabel}>LATEST RIPPLE</div>
                <p className={styles.featuredQuote}>
                  "<MentionText text={top.message} />"
                </p>
                <div className={styles.featuredMeta}>
                  <Avatar src={top.from_avatar} name={`${top.from_first_name} ${top.from_last_name}`} size="sm" />
                  <span className={styles.featuredNames}>{recipients} · from {top.from_first_name} {top.from_last_name}</span>
                  <div className={styles.featuredChipGroup}>
                    <span className={styles.featuredChip}>{t.label}</span>
                    <span className={styles.featuredWaves}>+{top.points || top.total_points}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Recent Ripples — list layout */}
          <div className={styles.listCard}>
            <div className={styles.listHeader}>
              <span className={styles.listTitle}>Recent ripples</span>
              <button className={styles.listAction} onClick={() => navigate('/feed')}>View all →</button>
            </div>
            {recentAppreciations.length === 0 ? (
              <p className={styles.emptyText}>No ripples yet</p>
            ) : (
              recentAppreciations.map((r) => {
                const t = RIPPLE_TYPES[r.type] || { label: r.type, emoji: '💖', color: '#0f7a68', bg: '#e8f5ee' };
                const recipients = r.recipients && r.recipients.length > 1
                  ? r.recipients.map((rec: any) => rec.to_first_name).join(', ')
                  : r.to_first_name;
                return (
                  <div key={r.id} className={styles.listRow} onClick={() => navigate(`/feed?highlight=${r.id}`)}>
                    <Avatar src={r.from_avatar} name={`${r.from_first_name} ${r.from_last_name}`} size="sm" />
                    <div className={styles.listRowContent}>
                      <div className={styles.listRowTop}>
                        <span className={styles.listRowFrom}>{r.from_first_name} {r.from_last_name}</span>
                        <span className={styles.listRowTo}>recognised {recipients}</span>
                        <span className={styles.listRowTime}>{formatRelativeTime(r.created_at)}</span>
                      </div>
                      <p className={styles.listRowMsg}><MentionText text={r.message} /></p>
                      <div className={styles.listRowChips}>
                        <span className={styles.listRowType} style={{ color: t.color, background: t.bg }}>{t.label}</span>
                        <span className={styles.listRowWaves}>+{r.points || r.total_points} waves</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Recent Announcements */}
          <div className={styles.listCard}>
            <div className={styles.listHeader}>
              <span className={styles.listTitle}>Recent announcements</span>
              <button className={styles.listAction} onClick={() => navigate('/announcements')}>View all →</button>
            </div>
            {recentAnnouncements.length === 0 ? (
              <p className={styles.emptyText}>No announcements yet</p>
            ) : (
              recentAnnouncements.map((a) => (
                <div key={a.id} className={styles.listRow} onClick={() => navigate('/announcements')}>
                  <Avatar src={a.author_avatar} name={`${a.author_first_name} ${a.author_last_name}`} size="sm" />
                  <div className={styles.listRowContent}>
                    <div className={styles.listRowTop}>
                      <span className={styles.listRowFrom} style={{ fontWeight: 600 }}>{a.title}</span>
                      <span className={styles.listRowTime}>{formatDate(a.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Recent Streams */}
          <div className={styles.listCard}>
            <div className={styles.listHeader}>
              <span className={styles.listTitle}>Recent streams</span>
              <button className={styles.listAction} onClick={() => navigate('/streams')}>View all →</button>
            </div>
            {recentStreams.length === 0 ? (
              <p className={styles.emptyText}>No streams yet</p>
            ) : (
              recentStreams.map((s) => (
                <div key={s.id} className={styles.listRow} onClick={() => navigate('/streams')}>
                  <Avatar src={s.author_avatar} name={`${s.author_first_name} ${s.author_last_name}`} size="sm" />
                  <div className={styles.listRowContent}>
                    <div className={styles.listRowTop}>
                      <span className={styles.listRowFrom}>{s.author_first_name} {s.author_last_name}</span>
                      {s.category_name && <span className={styles.listRowTo}>{s.category_name}</span>}
                      <span className={styles.listRowTime}>{formatDate(s.created_at)}</span>
                    </div>
                    <p className={styles.listRowMsg} style={{ fontFamily: 'var(--font-sans)', fontWeight: 600 }}>{s.title}</p>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>

        {/* Right Column */}
        <div className={styles.rightColumn}>
          {/* Upcoming Events */}
          {upcomingEvents.length > 0 && (
            <div className={styles.sidebarCard}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>
                  <div className={styles.calIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </div>
                  Coming up
                </h3>
                <button className={styles.sectionAction} onClick={() => navigate('/events')}>
                  View All <ArrowRight size={12} />
                </button>
              </div>
              <div className={styles.eventsGrid}>
                {upcomingEvents.slice(0, 3).map((ev) => {
                  const tc = getTypeConfig(ev.event_type || 'custom');
                  const diff = getDaysUntil(ev.event_date);
                  const evType = ev.event_type || 'custom';
                  const actionLabel = evType === 'birthday' ? 'Wish' : evType === 'work_anniversary' ? 'Celebrate' : 'RSVP';
                  const reaction = eventReactions[ev.id];
                  const reactionCount = reaction?.count || 0;
                  const userReacted = reaction?.userReacted || false;
                  return (
                    <div
                      key={ev.id}
                      className={styles.eventCard}
                      onClick={() => navigate('/events')}
                    >
                      <div className={styles.eventCardHeader}>
                        <div className={styles.eventTypeRow}>
                          <span className={styles.eventTypeDot} style={{ background: tc.color }} />
                          <span className={styles.eventTypeLabel}>{tc.label}</span>
                        </div>
                        <div className={styles.eventIconBadge} style={{ background: `${tc.color}15`, color: tc.color }}>
                          {(() => { const IC = getEventIconComponent(tc.icon_name); return <IC size={16} />; })()}
                        </div>
                      </div>
                      <div className={styles.eventTitle}>{ev.title}</div>
                      <div className={styles.eventDate}>{formatDate(ev.event_date)}</div>
                      <div className={styles.eventCardFooter}>
                        <span className={styles.eventCountdown}>
                          {diff <= 0 ? 'Today' : diff === 1 ? 'Tomorrow' : `In ${diff} days`}
                        </span>
                        <button
                          className={`${styles.eventActionBtn} ${userReacted ? styles.eventActionBtnActive : ''}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const idStr = String(ev.id);
                              const isSyn = idStr.startsWith('birthday-') || idStr.startsWith('anniversary-');
                              let res: any;
                              if (isSyn) {
                                const prefix = idStr.startsWith('birthday-') ? 'birthday-' : 'anniversary-';
                                const synType = prefix === 'birthday-' ? 'birthday' : 'work_anniversary';
                                const rest = idStr.slice(prefix.length).split('-');
                                const userId = rest.slice(0, 5).join('-');
                                const year = parseInt(rest[5], 10);
                                res = await eventsApi.toggleEventWish(userId, synType, year);
                              } else {
                                const reactionType = evType === 'birthday' ? 'wish' : evType === 'work_anniversary' ? 'celebrate' : 'rsvp';
                                res = await eventsApi.reactToEvent(ev.id, reactionType);
                              }
                              setEventReactions(prev => ({
                                ...prev,
                                [ev.id]: {
                                  count: res.data.count ?? res.data.wish_count ?? 0,
                                  userReacted: res.data.action !== 'removed' && res.data.action !== 'unwished',
                                },
                              }));
                            } catch { /* ignore */ }
                          }}
                        >
                          {actionLabel}
                        </button>
                        {reactionCount > 0 && (
                          <button
                            className={styles.eventCountLabel}
                            onClick={(e) => { e.stopPropagation(); handleViewReactions(ev.id, ev.title); }}
                          >
                            {reactionCount}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ripple Leaders */}
          <div className={styles.sidebarCard}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>
                <div className={styles.sectionIcon} style={{ background: 'linear-gradient(135deg, #D97706, #92400E)' }}>
                  <Trophy size={15} color="#fff" />
                </div>
                Ripple Leaders
              </h3>
            </div>
            <div className={styles.leaderTabs}>
              {[
                { key: 'received' as const, label: 'Most Recognized' },
                { key: 'given' as const, label: 'Top Givers' },
              ].map((t) => (
                <button
                  key={t.key}
                  className={`${styles.leaderTab} ${leaderTab === t.key ? styles.active : ''}`}
                  onClick={() => setLeaderTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {(() => {
              const list = leaderTab === 'received'
                ? (stats?.mostAppreciated || []).slice(0, 3)
                : (stats?.topAppreciators || []).slice(0, 3);
              const rankColors = [styles.leaderRank1, styles.leaderRank2, styles.leaderRank3];
              const maxWaves = list.length > 0
                ? Math.max(...list.map((p) => leaderTab === 'received' ? p.points_received : ((p as any).points_given || 0)), 1)
                : 1;
              return list.length === 0 ? (
                <p className={styles.emptyTextSmall}>No data yet</p>
              ) : (
                list.map((person, i) => {
                  const waves = leaderTab === 'received' ? person.points_received : ((person as any).points_given || 0);
                  const barPct = Math.max(10, (waves / maxWaves) * 100);
                  return (
                    <div key={person.id} className={styles.leaderItem}>
                      <span className={`${styles.leaderRank} ${rankColors[i]}`}>#{i + 1}</span>
                      <Avatar
                        src={person.avatar_url}
                        name={`${person.first_name} ${person.last_name}`}
                        size="sm"
                        className={styles.leaderAvatar}
                      />
                      <span className={styles.leaderName}>{person.first_name} {person.last_name}</span>
                      <div className={styles.leaderWavesCol}>
                        <span className={styles.leaderWaves}>{waves}</span>
                        <div className={styles.leaderWaveBarTrack}>
                          <div className={styles.leaderWaveBarFill} style={{ width: `${barPct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })
              );
            })()}
            <button className={styles.leaderboardBtn} onClick={() => navigate('/leaderboard')}>
              View Full Leaderboard →
            </button>
          </div>

        </div>
      </div>

      {/* Custom Date Picker Modal */}
      {showDatePicker && (
        <div className={styles.modalOverlay} onClick={() => setShowDatePicker(false)}>
          <div className={styles.datePickerModal} onClick={(e) => e.stopPropagation()}>
            <h3>Select Date Range</h3>
            <div className={styles.dateInputs}>
              <div className={styles.dateField}>
                <label>Start Date</label>
                <input
                  type="date"
                  value={customDates.start}
                  onChange={(e) => setCustomDates(prev => ({ ...prev, start: e.target.value }))}
                  className={styles.dateInput}
                />
              </div>
              <div className={styles.dateField}>
                <label>End Date</label>
                <input
                  type="date"
                  value={customDates.end}
                  onChange={(e) => setCustomDates(prev => ({ ...prev, end: e.target.value }))}
                  className={styles.dateInput}
                />
              </div>
            </div>
            <div className={styles.datePickerActions}>
              <button className={styles.datePickerCancel} onClick={() => setShowDatePicker(false)}>
                Cancel
              </button>
              <button
                className={styles.datePickerApply}
                onClick={handleCustomDateApply}
                disabled={!customDates.start || !customDates.end}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waves Ledger Modal */}
      <WavesLedgerModal
        isOpen={showLedgerModal}
        onClose={() => setShowLedgerModal(false)}
      />

      {/* Event Responses Modal */}
      {showReactionModal && (
        <Modal isOpen={showReactionModal} onClose={() => setShowReactionModal(false)} title="Event Responses">
          <div style={{ minWidth: 300 }}>
            <p style={{ fontSize: 13, color: '#8AA89C', marginBottom: 12, borderBottom: '1px solid #E2EDE9', paddingBottom: 8 }}>
              {reactionModalData.length} response{reactionModalData.length !== 1 ? 's' : ''} for "{reactionModalTitle}"
            </p>
            {reactionModalData.map((r: any, i: number) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
                <Avatar src={r.avatar_url} name={`${r.first_name} ${r.last_name}`} size="sm" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1A2E27' }}>{r.first_name} {r.last_name}</div>
                </div>
                <span style={{ fontSize: 11, color: '#8AA89C', textTransform: 'capitalize' as const }}>{r.reaction_type}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Dashboard;
